import moment from 'moment'
import { identity, isEmpty } from 'ramda'
import spacetime from 'spacetime'

import { OrgUsersById,User } from 'context/orgUsersContext'
import {
  Activity,
  ActivityContext,
  ActivityForContact,
  ActivityType,
  ScoredActivity,
  ScoredActivityType
} from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'

const isRecentActivity = (activityTime: number) => {
  const {
    diff: { years, months, days, hours, minutes }
  } = spacetime.now().since(activityTime)

  return minutes < 4 && years === 0 && months === 0 && days === 0 && hours === 0 // there is no science behind this number. plan is to replace this with presence data.
}

const getAgentDisplayName = (user: User | undefined) => {
  return user?.name || user?.email
}

export const participantsToString = (
  activityContext: ActivityContext,
  orgUsersById: OrgUsersById
) => {
  return activityContext?.agentParticipants
    ?.map(id => getAgentDisplayName(orgUsersById[id]))
    .filter(identity)
    .join(', ')
}

export const activityTypeToWord = (
  activity: Activity | ActivityForContact,
  orgUsersById: OrgUsersById
) => {
  if (activity.activityType === ActivityType.PAGE_VIEW) {
    return isRecentActivity(activity.createdAt) ? 'Is viewing' : 'Viewed'
  }
  if (activity.activityType === ActivityType.MEETING_BOOKED) {
    const meetingType = activity.activityContext?.meetingType
    const meetingTime = activity.activityContext?.slotStart
    const participants = participantsToString(activity.activityContext, orgUsersById)
    const meetingTypeStr = !!meetingType && ` "${meetingType}"`
    const meetingTimeStr =
      !!meetingTime && ` for ${moment(meetingTime).format('MMMM Do YYYY [at] h:mm A')}`
    const participantsStr = !isEmpty(participants) && ` with ${participants}`
    return `Booked a${meetingTypeStr} meeting${participantsStr}${meetingTimeStr}`
  }
  if (activity.activityType === ActivityType.MEETING_CANCELED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants) ? `Canceled a meeting` : `Canceled a meeting with ${participants}`
  }
  if (activity.activityType === ActivityType.MEETING_RESCHEDULED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants)
      ? `Rescheduled a meeting`
      : `Rescheduled a meeting with ${participants}`
  }
  if (activity.activityType === ActivityType.CONVERSATION) {
    return activity.activityContext.conversationPreview ? `Said:` : `Started a conversation`
  }
  if (activity.activityType === ActivityType.EMAIL_CLICK) {
    return activity.activityContext.subject
      ? `Clicked link in email campaign “${activity.activityContext.subject}”`
      : 'Clicked link in email campaign'
  }
  if (activity.activityType === ActivityType.EMAIL_OPEN) {
    return activity.activityContext.subject
      ? `Opened email campaign “${activity.activityContext.subject}”`
      : 'Opened email campaign'
  }
  if (activity.activityType === ActivityType.EMAIL_SEND) {
    return activity.activityContext.subject
      ? `Sent email “${activity.activityContext.subject}”`
      : `Sent an email`
  }
  if (activity.activityType === ActivityType.VIDEO) {
    const percentage = activity.activityContext.percentViewed
    return isRecentActivity(activity.createdAt)
      ? `Is watching`
      : `Watched ${percentage ? `${percentage}% of ` : ''}`
  }
  if (activity.activityType === ActivityType.END_USER_IDENTIFIED) {
    return 'Provided email address'
  }
}

export const activityTypeToStringDescription = (activity: Activity | ActivityForContact) => {
  if (activity.activityType === ActivityType.PAGE_VIEW) {
    return 'Site visit'
  }
  if (activity.activityType === ActivityType.MEETING_BOOKED) {
    return 'Meeting booked'
  }
  if (activity.activityType === ActivityType.MEETING_CANCELED) {
    return 'Meeting canceled'
  }
  if (activity.activityType === ActivityType.CONVERSATION) {
    return 'Conversation'
  }
  if (activity.activityType === ActivityType.EMAIL_CLICK) {
    return 'Email click'
  }
  if (activity.activityType === ActivityType.EMAIL_OPEN) {
    return 'Email open'
  }
  if (activity.activityType === ActivityType.EMAIL_SEND) {
    return 'Email send'
  }
  if (activity.activityType === ActivityType.VIDEO) {
    return 'Video watched'
  }
  if (activity.activityType === ActivityType.END_USER_IDENTIFIED) {
    return 'Email address provided'
  }
}

// these two are going away or will look very different soon

const isRecentUserActivity = (activity: ScoredActivity) => {
  const {
    diff: { years, months, days, hours, minutes }
  } = spacetime.now().since(activity.createdAt)

  return minutes < 4 && years === 0 && months === 0 && days === 0 && hours === 0 // there is no science behind this number. plan is to replace this with presence data.
}

export const userActivityTypeToWord = (activity: ScoredActivity, orgUsersById: OrgUsersById) => {
  if (
    activity.activityType
      .toLowerCase()
      .split('_')
      .some(p => p === 'view')
  ) {
    return isRecentUserActivity(activity) ? 'is viewing' : 'viewed'
  }
  if (activity.activityType === ScoredActivityType.MEETING_BOOKED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants) ? `booked a meeting` : `booked a meeting with ${participants}`
  }
  if (activity.activityType === ScoredActivityType.MEETING_CANCELED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants) ? `canceled a meeting` : `canceled a meeting with ${participants}`
  }
  if (activity.activityType === ScoredActivityType.CONVERSATION) {
    return activity.activityContext.conversationPreview
      ? `said: ${activity.activityContext.conversationPreview}`
      : `started a conversation`
  }
  if (activity.activityType === ScoredActivityType.EMAIL_CLICK) {
    return activity.activityContext.subject
      ? `clicked link in email “${activity.activityContext.subject}”`
      : `clicked link in email`
  }
  if (activity.activityType === ScoredActivityType.EMAIL_OPEN) {
    return activity.activityContext.subject
      ? `opened email “${activity.activityContext.subject}”`
      : `opened email`
  }
  if (activity.activityType === ScoredActivityType.VIDEO) {
    return isRecentUserActivity(activity)
      ? `is watching "${activity.activityContext.title}"`
      : `watched "${activity.activityContext.title}"`
  }
  if (activity.activityType === ScoredActivityType.END_USER_IDENTIFIED) {
    return 'provided their email'
  }
  return 'was active'
}

export const prefixUrlWithHttp = (link?: string): string | undefined => {
  if (!link) return link

  const VALID_PREFIXES = ['http://', 'https://', 'mailto:']

  for (const prefix of VALID_PREFIXES) {
    if (link.startsWith(prefix)) {
      return link
    }
  }

  return `http://${link}`
}

export const getActivityLabel = (type: string, count: number) => {
  switch (type) {
    case ActivityType.PAGE_VIEW:
      return pluralize('page view', count)
    case ActivityType.EMAIL_OPEN:
      return `${pluralize('email', count)} opened`
    case ActivityType.MEETING_BOOKED:
      return `${pluralize('meeting', count)} booked`
    case ActivityType.CONVERSATION:
      return pluralize('conversation', count)
    case ActivityType.VIDEO:
      return `${pluralize('video', count)} watched`
    default:
      return ''
  }
}
